/**
 * The signed-in customer's lab history (ACC-010, ACC-012): every uploaded report, its source and
 * date, the markers read from it, a link to the original file, and a way to add another report.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import type { AccountLabDocument, AccountLabObservation, AccountLabs, LabProvider } from "@lattice/core";
import { isPdf } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { ErrorState } from "@lattice/ui/ErrorState";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import type { StatusState } from "@lattice/ui/Status";
import type { UploadOutcome } from "../api.ts";
import { loadLabs, signedIn, uploadFile } from "../api.ts";
import type { Picked } from "../Upload.tsx";
import { useUploads } from "../Upload.tsx";
import { AccountShell, TRY_AGAIN_THEN_SUPPORT } from "./Shell.tsx";
const DATE = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeZone: "UTC"
});
const VALUE = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3
});
const PROVIDER: Readonly<Record<LabProvider, string>> = {
  function: "Function Health",
  superpower: "Superpower",
  rythm: "Rythm",
  quest: "Quest",
  labcorp: "Labcorp",
  other: "Another lab",
  unknown: "Uploaded"
};
function day(iso: string | null): string {
  if (iso === null) return "Date not on the report";
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
  return DATE.format(date);
}
function sourceOf(doc: AccountLabDocument): string {
  if (doc.source === "practitioner") return "Your clinic";
  if (doc.source === "import") return PROVIDER[doc.provider];
  return doc.provider === "unknown" ? "Uploaded" : PROVIDER[doc.provider];
}
function valueOf(row: AccountLabObservation): string {
  return row.valueNumeric === null ? row.valueText ?? "" : VALUE.format(row.valueNumeric);
}
function flagLabel(flag: AccountLabObservation["flag"]): string | null {
  if (flag === "low") return "Low";
  if (flag === "high") return "High";
  if (flag === "abnormal") return "Abnormal";
  return null;
}
function problem(file: File, reason: "too_large" | "unsupported_type" | "failed"): string {
  if (reason === "failed") return "This upload didn't finish. Check your connection and try again.";
  if (reason === "unsupported_type") {
    return "We read PDF, JPEG, PNG and HEIC files. Save this one as a PDF and add it again.";
  }
  return isPdf(file.name, file.type) ? "This PDF is over 25 MB. Save only the pages with your results, or photograph them." : "This photo is over 25 MB. Take it again at a smaller size.";
}
type FileState = {
  readonly kind: "waiting";
} | {
  readonly kind: "uploading";
  readonly fraction: number;
} | {
  readonly kind: "done";
  readonly outcome: UploadOutcome;
};
function reading(doc: AccountLabDocument): boolean {
  return doc.status === "stored" || doc.status === "extracting";
}
function useLabHistory(signedInNow: boolean) {
  const [labs, setLabs] = useState<AccountLabs | null>(null);
  const [failed, setFailed] = useState(false);
  const reload = () => {
    void loadLabs().then(next => {
      if (next === null) setFailed(true);else if (next !== "signed_out") {
        setFailed(false);
        setLabs(next);
      }
    }, () => setFailed(true));
  };
  useEffect(() => {
    if (!signedInNow) return;
    reload();
  }, [signedInNow]);
  const pending = labs?.documents.some(reading) ?? false;
  useEffect(() => {
    if (!pending) return;
    const id = setInterval(reload, 800);
    return () => clearInterval(id);
  }, [pending]);
  return {
    labs,
    reload,
    failed
  };
}
function useFreshUploads(picked: readonly Picked[], onSettled: () => void): ReadonlyMap<string, FileState> {
  const [states, setStates] = useState<ReadonlyMap<string, FileState>>(new Map());
  const seen = useRef<Set<string> | null>(null);
  const running = useRef(false);
  const queued = useRef<Picked[]>([]);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  const set = (id: string, state: FileState) => setStates(before => new Map(before).set(id, state));
  const drain = async () => {
    if (running.current) return;
    running.current = true;
    for (let next = queued.current.shift(); next; next = queued.current.shift()) {
      const entry = next;
      set(entry.id, {
        kind: "uploading",
        fraction: 0
      });
      const outcome = await uploadFile(entry.file, fraction => set(entry.id, {
        kind: "uploading",
        fraction
      }));
      set(entry.id, {
        kind: "done",
        outcome
      });
      if (outcome.status !== "rejected") settled.current();
    }
    running.current = false;
  };
  useEffect(() => {
    if (seen.current === null) {
      seen.current = new Set(picked.map(entry => entry.id));
      return;
    }
    const fresh = picked.filter(entry => !seen.current?.has(entry.id));
    for (const entry of fresh) {
      seen.current.add(entry.id);
      set(entry.id, {
        kind: "waiting"
      });
      queued.current.push(entry);
    }
    if (fresh.length > 0) void drain();
  }, [picked]);
  return states;
}
function MarkerRow({
  row
}: {
  readonly row: AccountLabObservation;
}) {
  const flag = flagLabel(row.flag);
  return <div className="x78zum5 x1qughib x1r0x45p">
      <span className="xeuugli">{row.name}</span>
      <span>
        <span>{valueOf(row)}</span>
        {row.unit !== null && <span className="x1jty25b xl4dl4e"> {row.unit}</span>}
        {flag !== null && <span className="xgjxun8 xl4dl4e xqu410z"> {flag}</span>}
      </span>
    </div>;
}
function documentNote(doc: AccountLabDocument): string | null {
  if (doc.status === "failed") return doc.failureReason ?? "We could not read this file. Try a clearer copy or a PDF.";
  if (reading(doc)) return "Reading this report.";
  if (doc.observations.length === 0) return "No markers were read from this report.";
  return null;
}
function DocumentCard({
  doc
}: {
  readonly doc: AccountLabDocument;
}) {
  const note = documentNote(doc);
  return <li className="x78zum5 xdt5ytf x626il7 xfjredr xez8t42 x13fuv20 x178xt8z" data-testid="lab-document">
      <h2 className="x1ghz6dp xqu410z">{doc.filename}</h2>
      <p className="x1ghz6dp x1jty25b xl4dl4e">
        {day(doc.collectedOn ?? doc.uploadedOn)} · {sourceOf(doc)}
      </p>
      <a href={`/api/account/labs/${encodeURIComponent(doc.id)}/file`} target="_blank" rel="noreferrer" className="x10dbofl xqu410z x1hl2dhg x1gg8mnh x3nfvp2 x6s0dn4">
        Open original
      </a>
      {note !== null ? <p className="x1ghz6dp x1jty25b x1fzhlzt">{note}</p> : <div className="x78zum5 xdt5ytf x1173whf" data-testid="lab-markers">
          {doc.observations.map(row => <MarkerRow key={row.id} row={row} />)}
        </div>}
    </li>;
}
function Incoming({
  entry,
  state
}: {
  readonly entry: Picked;
  readonly state: FileState | undefined;
}) {
  const refused = state?.kind === "done" && state.outcome.status === "rejected" ? state.outcome.reason : null;
  return <li className="x78zum5 xdt5ytf x1173whf x1wiri7k x1t74ka8 x1q0q8m5 xso031l" data-testid="upload-row">
      <span className="xqu410z">{entry.file.name}</span>
      {state?.kind === "uploading" ? <span className="xl4dl4e x1jty25b x1l1vl4p">{Math.round(state.fraction * 100)}%</span> : refused ? <span className="xl4dl4e x1jty25b x1l1vl4p">Not added</span> : state?.kind === "done" ? <span className="xl4dl4e x1jty25b x1l1vl4p">Received</span> : <span className="xl4dl4e x1jty25b x1l1vl4p">Waiting</span>}
      {refused && <p role="alert" className="x1ghz6dp xl4dl4e xgjxun8 x1fzhlzt">
          {problem(entry.file, refused)}
        </p>}
    </li>;
}
function activity(incoming: readonly Picked[], states: ReadonlyMap<string, FileState>, labs: AccountLabs | null): {
  readonly message: string;
  readonly state: StatusState;
} | null {
  const uploading = incoming.find(entry => states.get(entry.id)?.kind === "uploading");
  if (uploading) return {
    message: `Uploading ${uploading.file.name}`,
    state: "pending"
  };
  const readingNow = labs?.documents.find(reading);
  if (readingNow) return {
    message: `Reading ${readingNow.filename}`,
    state: "pending"
  };
  if (labs?.hasApprovedFormula && labs.reviewInProgress) {
    return {
      message: "Your current formula keeps shipping while a physician reviews the new labs.",
      state: "ok"
    };
  }
  return null;
}
function History({
  labs
}: {
  readonly labs: AccountLabs;
}) {
  if (labs.documents.length === 0) {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">No lab reports yet. Upload a PDF, photo or screenshot.</p>;
  }
  return <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Lab history" data-testid="lab-history">
      {labs.documents.map(doc => <DocumentCard key={doc.id} doc={doc} />)}
    </ul>;
}
export function AccountLabs() {
  const [session, setSession] = useState<"unknown" | "out" | "in" | "failed">("unknown");
  const {
    picked,
    choose,
    takePhoto
  } = useUploads();
  const signedInNow = session === "in";
  const {
    labs,
    reload,
    failed
  } = useLabHistory(signedInNow);
  const states = useFreshUploads(picked, reload);
  const incoming = picked.filter(entry => {
    const state = states.get(entry.id);
    return state !== undefined && (state.kind !== "done" || state.outcome.status === "rejected");
  });
  const now = activity(incoming, states, labs);
  useEffect(() => {
    document.title = "Your labs";
    void signedIn().then(ok => setSession(ok ? "in" : "out"), () => setSession("failed"));
  }, []);
  if (session === "unknown") {
    return <AccountShell title="Your labs">
        <Pending message="Opening your labs" bones={["title", "line", "line"]} />
      </AccountShell>;
  }
  if (session === "failed") {
    return <AccountShell title="Your labs">
        <ErrorState focusOnShow title="We couldn't open your labs" body={TRY_AGAIN_THEN_SUPPORT} onRetry={() => window.location.reload()} />
      </AccountShell>;
  }
  if (session === "out") {
    return <AccountShell title="Your labs" signedOut gateTitle="Sign in to see your labs">
        {null}
      </AccountShell>;
  }
  return <AccountShell title="Your labs">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">
        Every report you've uploaded, with the markers we read. Upload a new one at any time.
      </p>
      <div className="x78zum5 x1a02dak x626il7">
        <Button testId="upload-new-labs" onClick={choose}>
          Upload new labs
        </Button>
        <Button tone="quiet" onClick={takePhoto}>
          Take a photo
        </Button>
      </div>
      {now && <Status state={now.state}>{now.message}</Status>}
      {incoming.length > 0 && <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Uploading">
          {incoming.map(entry => <Incoming key={entry.id} entry={entry} state={states.get(entry.id)} />)}
        </ul>}
      {labs === null && failed ? <ErrorState focusOnShow title="We couldn't load your reports" body={TRY_AGAIN_THEN_SUPPORT} onRetry={() => {
      reload();
    }} /> : labs === null ? <Pending message="Loading your reports" bones={["title", "line", "line"]} /> : <History labs={labs} />}
    </AccountShell>;
}